/* eslint-disable @typescript-eslint/no-unused-vars */
import { RequestDetailsVM, RequestStatusEnum } from "@payping/api";
import { FiltersType, GridifyConditionEnum, GridifyLogicalEnum, createQuery } from "@payping/utils/api";
import { getCashOutRequests } from "@payping/redux/actions";
import { dateFormats, toJalali } from "@payping/utils/dateUtils";
import { DashboardRoutes } from "@/router/constant";
import { submitQueryParams } from "@payping/utils/query-string";
import { addOverflow } from "@payping/utils/string";
import { Copy, Space, Table, TableAction, Tag, Tooltip, useMessagedAsyncRetry, Flex } from "@payping/web-components";
import { ColumnsType, TablePaginationConfig } from "@payping/web-components/components/Table/types";
import classnames from "classnames";
import qs from "query-string";
import React, { ReactElement } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAppTranslation } from "@/utils/useAppTranslation";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { requestListId, tableRowId } from "@/assets/googleTrackIds";
import styles from "../../../../assets/scss/Settlement/Settlement.module.scss";
import { RequestType, SettlementListNumberQueryParamsType, SettlementListStringQueryParamsType } from "../types";
import { RequestListFilters } from "../RequestListFilter";
import { CuToman } from "@payping/icons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";

export interface SettlementRowType {
  key: React.Key;
  amount?: string;
  code?: string;
  repayDate?: string;
  reqDate?: string;
  status?: string;
  statusColor?: string;
}

const RequestsReport: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { t } = useAppTranslation();
  const { requestList } = useAppSelector((state) => state.cashOuts);
  const navigate = useNavigate();
  const parsedSearch = qs.parse(location.search, { arrayFormat: "bracket" });
  const { code, submittedTimeFrom, submittedTimeTo }: SettlementListStringQueryParamsType = qs.parse(location.search, {
    arrayFormat: "bracket",
  });
  const {
    pageNumber = 1,
    pageSize = 10,
    codeOp,
    status,
    statusOp,
  }: SettlementListNumberQueryParamsType = qs.parse(location.search, {
    parseNumbers: true,
    arrayFormat: "bracket",
  });

  const { loading, retry: retrySettlment } = useMessagedAsyncRetry(
    async () => {
      const filter: FiltersType = [
        {
          status: [statusOp || GridifyConditionEnum.Equal, status || [3, 4, 5], { parenthesis: true }],
          code: [codeOp || GridifyConditionEnum.Equal, code],
          submittedTime: [GridifyConditionEnum.GreaterThan, submittedTimeFrom],
        },
        GridifyLogicalEnum.And,
        { submittedTime: [GridifyConditionEnum.LessThan, submittedTimeTo] },
      ];
      await dispatch(getCashOutRequests(pageNumber, pageSize, undefined, createQuery(filter)));
    },
    [code, submittedTimeFrom, submittedTimeTo, pageSize, pageNumber, requestList?.count],
    { success: false, useProgress: true },
  );

  const pagination: TablePaginationConfig = {
    "data-test": "report-request-list-pagination",
    onChange: (page, newSize): void => {
      const newValues = {
        ...parsedSearch,
        pageNumber: page,
        pageSize: newSize,
      };
      submitQueryParams({
        navigate,
        newValues,
        parsedSearch,
        pathname: location.pathname,
        retry: retrySettlment,
      });
    },
    onShowSizeChange: (_currentPage, newSize): void => {
      const newValues = {
        ...parsedSearch,
        pageNumber: 1,
        pageSize: newSize,
      };
      submitQueryParams({
        navigate,
        newValues,
        parsedSearch,
        pathname: location.pathname,
        retry: retrySettlment,
      });
    },
    pageNumber,
    pageSize,
    showTotal: (total, range): string => t("showTotal", { range: `${range[0]}-${range[1]}`, total }),
    total: requestList?.count,
  };
  const columns: ColumnsType<RequestDetailsVM> = [
    {
      dataIndex: "title",
      key: "title",
      render(value) {
        return (
          <Tooltip title={value}>
            <span>{addOverflow(value || "-", 30)}</span>
          </Tooltip>
        );
      },
      title: t("settlement.fileName"),
    },
    {
      dataIndex: "submittedTime",
      key: "submittedTime",
      render(value) {
        return <span>{toJalali({ date: value, toFormat: dateFormats.ui.jalaliDateTime }).string}</span>;
      },
      title: t("settlement.requestDate"),
    },
    {
      dataIndex: "totalAmount",
      key: "totalAmount",
      render(totalAmount: string) {
        return (
          <Flex align="center">
            <span>{formatLocalizedNumber({ value: Number(totalAmount) })}</span>
            <CuToman width={24} height={24} />
          </Flex>
        );
      },
      title: t("settlement.totalCashout"),
    },
    {
      dataIndex: "code",
      key: "code",
      render(_, tableData) {
        return <Copy text={tableData.code || ""}>{tableData.code}</Copy>;
      },
      title: t("settlement.code"),
    },
    {
      dataIndex: "status",
      key: "status",
      render(value, record) {
        const tagColor = () => {
          switch (value) {
            case RequestStatusEnum.Unknown:
              return "orange";
            case RequestStatusEnum.Final:
              return "green";
            case RequestStatusEnum.Failed:
              return "red";
            default:
              return undefined;
          }
        };
        return (
          <Tag color={tagColor()}>
            {t(`settlement.request.status.${RequestStatusEnum[value]}`, {
              failedCount: record.failedCount,
              inProgressCount: record.inProgressCount,
              succeededCount: record.succeededCount,
            })}
          </Tag>
        );
      },
      title: t("settlement.report.status"),
    },

    {
      dataIndex: "settlementsStatus",
      key: "settlementsStatus",
      render(_, record) {
        return (
          <span className={classnames(styles.space)}>
            {t("settlement.requestStatus", {
              failedCount: record.failedCount,
              inProgressCount: record.inProgressCount,
              succeededCount: record.succeededCount,
            })}
          </span>
        );
      },
      title: t("settlement.settlementsStatus"),
    },
    {
      dataIndex: "totalWage",
      key: "totalWage",
      render(totalWage: string) {
        return (
          <Flex align="center">
            <span>{formatLocalizedNumber({ value: Number(totalWage) })}</span>
            <CuToman width={24} height={24} />
          </Flex>
        );
      },
      title: t("settlement.totalWage"),
    },
  ];
  const actions = (rowIndex: number): ReactElement[] => [
    <a key={1}>
      <TableAction
        iconColor={"primary"}
        label={t("settlement.view")}
        onClick={() => {
          if (rowIndex !== undefined && requestList !== undefined) {
            navigate({
              pathname: DashboardRoutes.settlement.report.detail.url.replace(
                ":code",
                requestList?.data?.[rowIndex]?.code || "",
              ),
            });
          }
        }}
        key={2}
        data-google-track={requestListId}
      />
    </a>,
  ];

  return (
    <div>
      <Space direction={"vertical"} size={"middle"} className={"tw-w-full"}>
        <RequestListFilters retry={retrySettlment} loading={loading} />
        <Table<RequestType>
          actions={actions}
          hasIdColumn
          dataSource={requestList?.data}
          columns={columns}
          pagination={pagination}
          loading={loading}
          components={{
            body: { row: (rowProps: Record<string, unknown>) => <tr {...rowProps} data-google-track={tableRowId} /> },
          }}
        />
      </Space>
    </div>
  );
};

export default RequestsReport;
